import { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import TimeOutModal from '@uhg-uicl/ui-core/dist/TimeOutModal';
import { push } from 'connected-react-router';

import {
  setNotificationBanner,
  setUser,
  toggleAuthModalOpen,
} from '../../PageWrapper/modules/actions';
import { selectAuthModalOpen } from '../../PageWrapper/modules/selectors';
import { useIdleTimer } from 'react-idle-timer';
import { useAuth } from 'react-oidc-context';
import { BaseRoute } from 'constants/routes';
import { UserProfile } from '../modules/types';
import { notificationMessages, notificationTypes } from 'constants/commonConstants';
import { LocalStorageKeys } from '../modules/constants';
import { getLoggedInUser } from '../modules/utils';

type SilentRefreshModalType = {
  authModalOpen: boolean;
  dispatchToggleAuthModalOpen: Function;
  dispatchSetUser: Function;
  dispatchPush: Function;
  dispatchSetNotificationBanner: Function;
};

const SilentRefreshModal = ({
  authModalOpen,
  dispatchToggleAuthModalOpen,
  dispatchSetUser,
  dispatchPush,
  dispatchSetNotificationBanner,
}: SilentRefreshModalType) => {
  const auth = useAuth();
  // Total user idle time is 120 minutes (2 hours)
  const minutesBeforePrompt = 115;
  const minutesAfterPrompt = 5;
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const [isSilentUpdateEnable, setIsSilentUpdateEnable] = useState<boolean>(false);
  const [isSessionExpired, setIsSessionExpired] = useState<boolean>(false);
  const [acceptedPrompt, setAcceptedPrompt] = useState<boolean>(true);
  const [tokenFailed, setTokenFailed] = useState<boolean>(false);

  const forceLogout = () => {
    try {
      dispatchPush(`${BaseRoute}/logout`);
    } catch (e) {
      window.location.href = `${BaseRoute}/logout`;
    }
  };

  const openPrompt = () => {
    if (!isSessionExpired) {
      setAcceptedPrompt(false);
      dispatchToggleAuthModalOpen(true);
      // Open a new timer based on minutesAfterPrompt, if the user does not respond to the prompt, log them out.
      setTimeout(
        () => {
          acceptedPrompt ? setAcceptedPrompt(false) : forceLogout();
        },
        minutesAfterPrompt * 60 * 1000,
      );
    } else {
      forceLogout();
    }
  };

  const getExpirationTime = () => {
    return auth.user?.expires_in ?? 5;
  };

  const refresh = () => {
    setIsRefreshing(true);
    auth.startSilentRenew();
    auth
      .signinSilent()
      .then((response) => {
        if (response) {
          // Set User in store
          const loggedInUser: UserProfile = getLoggedInUser(auth);
          dispatchSetUser(loggedInUser);
          localStorage.setItem(LocalStorageKeys.user_access_token, auth.user?.access_token ?? '');
          setIsRefreshing(false);
        } else {
          dispatchSetNotificationBanner(
            true,
            notificationMessages.generalLogout,
            notificationTypes.negative,
          );
          setTokenFailed(true);
          // force a logout since we cannot refresh the token
          forceLogout();
        }
      })
      .catch((err) => {
        setIsSessionExpired(true);
        setIsRefreshing(false);
      });
    auth.stopSilentRenew();
    dispatchToggleAuthModalOpen(false);
    setIsSessionExpired(false);
    setIsRefreshing(false);
    reset();
  };

  const staySignedIn = () => {
    setAcceptedPrompt(true);
    refresh();
  };

  useEffect(() => {
    if (isSilentUpdateEnable) {
      refresh();
      setIsSilentUpdateEnable(false);
    }
  }, [isSilentUpdateEnable]);

  const onAction = (event) => {
    const expirationTimer = getExpirationTime() || 0;
    const timeBeforeExpiration = expirationTimer * 1000;
    const totalIdleTime = (minutesBeforePrompt + minutesAfterPrompt) * 60 * 1000;

    // Attempt to silently refresh if the user is still active, and the login session will expire within the total idle time.
    if (
      !isRefreshing &&
      !isSilentUpdateEnable &&
      timeBeforeExpiration < totalIdleTime &&
      // Only refresh if less than 10 minutes remain on the session.
      expirationTimer < 600 &&
      tokenFailed === false &&
      !authModalOpen
    ) {
      setIsSilentUpdateEnable(true);
    }
  };

  const { reset } = useIdleTimer({
    onAction,
    timeout: minutesBeforePrompt * 60 * 1000,
    onIdle: () => {
      openPrompt();
    },
    stopOnIdle: true,
    events: [
      'mousemove',
      'keydown',
      'wheel',
      'DOMMouseScroll',
      'mousewheel',
      'mousedown',
      'touchstart',
      'touchmove',
      'MSPointerDown',
      'MSPointerMove',
      'visibilitychange',
    ],
    debounce: 250,
    throttle: 0,
    eventsThrottle: 200,
    element: document,
    startOnMount: true,
    startManually: false,
    crossTab: false,
    promptBeforeIdle: 0,
  });

  return (
    <TimeOutModal
      isOpen={authModalOpen}
      onSignOut={forceLogout}
      onStaySignedIn={staySignedIn}
      minutesUntilSignOut={minutesAfterPrompt}
      disableSignOutButton={isRefreshing}
      disableStaySignedInButton={isRefreshing}
    />
  );
};

const mapDispatchToProps = (dispatch: any) => ({
  dispatchToggleAuthModalOpen: (open: boolean) => dispatch(toggleAuthModalOpen(open)),
  dispatchSetUser: (user: any) => dispatch(setUser(user)),
  dispatchPush: (location: any) => dispatch(push(location)),
  dispatchSetNotificationBanner: (
    notificationVisible: boolean,
    notificationMessage: string,
    notificationType: string,
  ) => dispatch(setNotificationBanner(notificationVisible, notificationMessage, notificationType)),
});

const mapStateToProps = (state: any) => ({
  authModalOpen: selectAuthModalOpen(state),
});

export default connect(mapStateToProps, mapDispatchToProps)(SilentRefreshModal);
